import axios from 'axios'
import qs from 'qs'
import FileSaver from 'file-saver'
import fn from '../../utils/functions'
import dayjs from 'dayjs';

// Normaliza fechas en profundidad para evitar que qs.stringify destruya objetos Dayjs
export function convertDates(obj) {
  if (obj == null) return obj;

  // Detecta Day.js correctamente
  if (dayjs.isDayjs(obj)) {
    return obj.toISOString();
  }

  // Detecta Date nativo
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Arrays → recorrer
  if (Array.isArray(obj)) {
    return obj.map(item => convertDates(item));
  }

  // Objetos → recorrer recursivamente
  if (typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      newObj[key] = convertDates(obj[key]);
    }
    return newObj;
  }

  
  return obj;
}

// -----------------------------
// Estado inicial (sesión)
// -----------------------------
function initSession () {
  const session = {
    coacsu: {
      tableData: [],
      totalCoacsu: 0,
      detail: null // NUEVO: detalle actual
    },
    coacsuDetail: null,
    coacsuDraft: null,
    queryCoacsu: {
      page: 0,
      size: 20,
      sort: 'creationDateTime,DESC',
      creationDateTimeFrom: fn.initDateFrom(3),
      creationDateTimeTo: fn.initDateTo()
    },
    listSelectedFiltersCoacsu: [],
    // combos principales
    optionsFilterServiceCoacsu: [],
    optionsFilterSellerCompanyCoacsu: [],
    optionsFilterBuyerCompanyCoacsu: [],
    optionsFilterPointOfSaleCoacsu: [],
    optionsFilterDocumentNumberCoacsu: [],
    optionsFilterSaleDateCoacsu: [],
    optionsFilterArticleCodeCoacsu: []
  }
  return session
}

// -----------------------------
// Estado inicial (local - UI)
// -----------------------------
function initLocal () {
  const local = {
    // columnas tabla principal
    isShowServiceCoacsu: true,
    isShowSellerCompanyCoacsu: true,
    isShowBuyerCompanyCoacsu: true,
    isShowPointOfSaleCoacsu: true,
    isShowDocumentNumberCoacsu: true,
    isShowDocumentDateCoacsu: true,
    isShowCreationDateTimeCoacsu: true,
    isShowTotalAmountCoacsu: true,

    // columnas tabla DETALLE coacsu (NUEVO)
    columnsDetailCoacsu: {
      lineNumber: true,
      invoiceNumber: true,
      issueDate: true,
      paymentDate: true,
      total: true,
      currency: true
    }
  }
  return local
}

// -----------------------------
// Carga de prefs por usuario
// -----------------------------
function setByUserLocalCoacsu (username) {
  try {
    const raw = localStorage.getItem(username)
    if (!raw) return initLocal()
    const parsed = JSON.parse(raw)
    const base = initLocal()
    const fromUser = parsed?.coacsu || {}

    // Mezcla segura manteniendo defaults cuando falten claves
    return {
      ...base,
      ...fromUser,
      columnsDetailCoacsu: {
        ...base.columnsDetailCoacsu,
        ...fromUser.columnsDetailCoacsu
      }
    }
  } catch (e) {
    console.log('error', e)
    return initLocal()
  }
}

// -----------------------------
// state
// -----------------------------
const state = {
  userConfigLocalStorage: 'B2B_DEFAULT_USER', // valor dummy seguro
  local: initLocal(),
  session: initSession()
}

// -----------------------------
// getters
// -----------------------------
const getters = {
  // tabla principal
  coacsu: state =>
    (state.session?.coacsu) || { tableData: [], totalCoacsu: 0, detail: null },

  queryCoacsu: state =>
    (state.session?.queryCoacsu) || { page: 0, size: 20, sort: 'creationDateTime,DESC' },

  listSelectedFiltersCoacsu: state => (state.session?.listSelectedFiltersCoacsu) || [],

  // combos
  optionsFilterServiceCoacsu: state => (state.session?.optionsFilterServiceCoacsu) || [],
  optionsFilterSellerCompanyCoacsu: state => (state.session?.optionsFilterSellerCompanyCoacsu) || [],
  optionsFilterBuyerCompanyCoacsu: state => (state.session?.optionsFilterBuyerCompanyCoacsu) || [],
  optionsFilterPointOfSaleCoacsu: state => (state.session?.optionsFilterPointOfSaleCoacsu) || [],
  optionsFilterDocumentNumberCoacsu: state => (state.session?.optionsFilterDocumentNumberCoacsu) || [],
  optionsFilterSaleDateCoacsu: state => (state.session?.optionsFilterSaleDateCoacsu) || [],
  optionsFilterArticleCodeCoacsu: state => (state.session?.optionsFilterArticleCodeCoacsu) || [],

  // columnas tabla principal
  isShowServiceCoacsu: state => (state.local?.isShowServiceCoacsu) ?? true,
  isShowSellerCompanyCoacsu: state => (state.local?.isShowSellerCompanyCoacsu) ?? true,
  isShowBuyerCompanyCoacsu: state => (state.local?.isShowBuyerCompanyCoacsu) ?? true,
  isShowPointOfSaleCoacsu: state => (state.local?.isShowPointOfSaleCoacsu) ?? true,
  isShowDocumentNumberCoacsu: state => (state.local?.isShowDocumentNumberCoacsu) ?? true,
  isShowDocumentDateCoacsu: state => (state.local?.isShowDocumentDateCoacsu) ?? true,
  isShowSaleDateCoacsu: state => (state.local?.isShowSaleDateCoacsu) ?? true,
  isShowArticleCodeCoacsu: state => (state.local?.isShowArticleCodeCoacsu) ?? true,
  isShowQuantitySoldCoacsu: state => (state.local?.isShowQuantitySoldCoacsu) ?? true,
  isShowQuantityReturnedCoacsu: state => (state.local?.isShowQuantityReturnedCoacsu) ?? true,
  isShowCreationDateTimeCoacsu: state => (state.local?.isShowCreationDateTimeCoacsu) ?? true,
  isShowTotalAmountCoacsu: state => (state.local?.isShowTotalAmountCoacsu) ?? true,

  // DETALLE (NUEVO)
  coacsuDetail: state => (state.session?.coacsuDetail) || null,
  coacsuDraft: state => (state.session?.coacsuDraft) || null,

  isShowLineNumber:    state => state.local.columnsDetailCoacsu.lineNumber,
  isShowInvoiceNumber: state => state.local.columnsDetailCoacsu.invoiceNumber,
  isShowIssueDate:     state => state.local.columnsDetailCoacsu.issueDate,
  isShowPaymentDate:   state => state.local.columnsDetailCoacsu.paymentDate,
  isShowTotal:         state => state.local.columnsDetailCoacsu.total,
  isShowCurrency:      state => state.local.columnsDetailCoacsu.currency
}

// -----------------------------
// actions
// -----------------------------
const actions = {
  setUserNameCoacsu ({ state }) {
    const B2bUserConfigSession = JSON.parse(sessionStorage.getItem('store'))
    state.userConfigLocalStorage = B2bUserConfigSession.activeUser.data.username
    if (localStorage.getItem(state.userConfigLocalStorage)) {
      state.local = setByUserLocalCoacsu(state.userConfigLocalStorage)
    }
  },

  async getCoacsus ({ commit }, data) {
    try {
      // Solo enviar los parámetros activos del filtro
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/coacsu',
        params: {
          ...data.query
        },
        headers: { 'Accept-Language': data.activeLang },
        paramsSerializer: params => {
          return qs.stringify(convertDates(params), { indices: false})
        }
      })
      commit('COACSU_STATE', {
        tableData: response.data.content,
        totalCoacsu: response.data.totalElements
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  // Servicio para descargar coacsu report en formato CSV
  async downloadCoacsuCsv ({ rootState }, params) {
    try {
      params.urlName = null
      if (rootState.session.urlIn && rootState.session.urlIn !== 'b2b') {
        params.urlName = rootState.session.urlIn
      }

      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/coacsu/csv',
        params,
        responseType: 'blob',
        headers: { 'Accept-Language': params.activeLang },
        paramsSerializer: p => qs.stringify(convertDates(p), { indices: false
          })
      })

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      })

      // nombre por defecto


      const filename = fn.extractFilename(response.headers['content-disposition'], 'coacsu.csv')


      FileSaver.saveAs(blob, filename)

      // refresca JWT igual que en REMADV
      fn.assignJwt(null, rootState.session.jwt, response.headers.authorization)

      return filename
    } catch (error) {
      throw error?.response || error
    }
  },

  // Servicio para descargar coacsu en formato original (ej: XML, PDF, etc)
  async downloadCoacsuOriginal ({ commit, rootState }, { coacsuId, activeLang }) {
    try {
      let urlName = null
      if (
        rootState.session.urlIn !== null &&
        rootState.session.urlIn !== undefined &&
        rootState.session.urlIn !== 'b2b'
      ) {
        urlName = rootState.session.urlIn
      }

      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: `/coacsu/${coacsuId}/original`,
        params: urlName ? { urlName } : {},
        responseType: 'blob',
        headers: { 'Accept-Language': activeLang }
      })

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      })
      let filename = 'coacsu-original.zip'
      const fileGroup = /([^;]+;_+)?filename="([^"]+)"$/.exec(
        response.headers['content-disposition']
      )
      if (fileGroup?.[2]) {
        filename = fileGroup[2]
      }
      FileSaver.saveAs(blob, filename)

      fn.assignJwt(commit, rootState.session.jwt, response.headers.authorization)
      return filename
    } catch (error) {
      throw error.response
    }
  },

  // Servicio para descargar metadata de coacsu
  // Servicio para descargar metadata de coacsu
  async downloadCoacsuMetadata ({ commit, rootState }, { coacsuId, activeLang }) {
    try {
      let urlName = null
      if (rootState.session.urlIn !== null &&
          rootState.session.urlIn !== undefined &&
          rootState.session.urlIn !== 'b2b') {
        urlName = rootState.session.urlIn
      }

      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: `/coacsu/${coacsuId}/metadata`,        // <<< usa el {id} del path
        params: urlName ? { urlName } : {},         // solo si hace falta
        responseType: 'blob',
        headers: { 'Accept-Language': activeLang },
        paramsSerializer: params => qs.stringify(convertDates(params), { indices: false, serializeDate: (date) => date instanceof Date ? date.toISOString() : date })
      })

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      })

      let filename = 'coacsu-metadata.zip'
      const fileGroup = /([^;]+;_+)?filename="([^"]+)"$/.exec(
        response.headers['content-disposition'] || ''
      )
      if (fileGroup?.[2]) {
        filename = fileGroup[2]
      }

      FileSaver.saveAs(blob, filename)
      fn.assignJwt(commit, rootState.session.jwt, response.headers.authorization)

      return filename
    } catch (error) {
      throw error.response || error
    }
  },

  // -----------------------------
  // DETALLE coacsu (NUEVO)
  // -----------------------------
  async getCoacsuDetail ({ commit, rootState }, { coacsuId }) {
    const response = await axios({
      method: 'GET',
      baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
      url: `/coacsu/${coacsuId}/detail`,
      headers: { 'Accept-Language': (rootState.session?.lang) || 'es' }
    })
    commit('COACSU_DETAIL_STATE', response.data)
    return response.data
  },

  // Métodos para limpiar y actualizar filtros activos
  async listSelectedFiltersCoacsuState ({ commit }, payload) {
    commit('LIST_SELECTED_FILTERS_COACSU_STATE', payload)
  },
  async queryCoacsuState ({ commit }, payload) {
    commit('QUERY_COACSU_STATE', payload)
  },
  async coacsuState ({ commit }, payload) {
    commit('COACSU_STATE', payload)
  },

  // Métodos para columnas visibles (tabla principal)
  isShowServiceCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_SERVICE_COACSU', payload)
  },
  isShowSellerCompanyCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_SELLER_COMPANY_COACSU', payload)
  },
  isShowBuyerCompanyCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_BUYER_COMPANY_COACSU', payload)
  },
  isShowPointOfSaleCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_POINT_OF_SALE_COACSU', payload)
  },
  isShowDocumentNumberCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_DOCUMENT_NUMBER_COACSU', payload)
  },
  isShowDocumentDateCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_DOCUMENT_DATE_COACSU', payload)
  },
  isShowSaleDateCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_SALE_DATE_COACSU', payload)
  },
  isShowArticleCodeCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_ARTICLE_CODE_COACSU', payload)
  },
  isShowQuantitySoldCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_QUANTITY_SOLD_COACSU', payload)
  },
  isShowQuantityReturnedCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_QUANTITY_RETURNED_COACSU', payload)
  },
  isShowCreationDateTimeCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_CREATION_DATE_TIME_COACSU', payload)
  },
  isShowTotalAmountCoacsuAction: ({ commit }, payload) => {
    commit('IS_SHOW_TOTAL_AMOUNT_COACSU', payload)
  },

  // Flags de columnas DETALLE (NUEVO)
  setShowLineNumber     ({ commit }, payload) { commit('SET_SHOW_LINE_NUMBER', payload) },
  setShowInvoiceNumber  ({ commit }, payload) { commit('SET_SHOW_INVOICE_NUMBER', payload) },
  setShowIssueDate      ({ commit }, payload) { commit('SET_SHOW_ISSUE_DATE', payload) },
  setShowPaymentDate    ({ commit }, payload) { commit('SET_SHOW_PAYMENT_DATE', payload) },
  setShowTotal          ({ commit }, payload) { commit('SET_SHOW_TOTAL', payload) },
  setShowCurrency       ({ commit }, payload) { commit('SET_SHOW_CURRENCY', payload) },

  async createCoacsuFromInvoices ({ rootState, getters, state }, { invoiceNoteIds, activeLang, force }) {
    try {
      const draft = state.session?.coacsuDraft
      const data = { invoiceNoteIds };
      if (force) data.force = true;
      if (draft) {
        if (draft.buyerId) data.buyerId = draft.buyerId
        if (draft.centreBuyerInvoicee) data.centreBuyerInvoiceeCode = draft.centreBuyerInvoicee
        if (draft.sellerId) data.sellerId = draft.sellerId
      }
      const response = await axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/coacsu/create',
        data,
        headers: {
          'Accept-Language': activeLang || (rootState.session?.lang) || 'es',
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },
  setCoacsuDraft ({ commit }, payload) {
    commit('SET_COACSU_DRAFT', payload)
  },

  async getDocumentFormats () {
    try {
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/constant/documenttype/format'
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async getPaymentMeansByFormatDocumentId (_, { formatDocumentId }) {
    try {
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/constant/paymentmeans',
        params: { formatDocumentId }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async getCurrencies () {
    try {
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/constant/currency'
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async getProjectParty (_, { partyId }) {
    try {
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: `/party/${partyId}/projectparty`
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async searchInvoicesForCoacsu (_, { sellerId, buyerId, page = 0, size = 100 }) {
    try {
      const params = {
        searchType: 'EMITTED',
        emission: true,
        page,
        size,
        creationDateTimeFrom: fn.initDateFrom(36),
        creationDateTimeTo: fn.initDateTo()
      }
      if (sellerId) params.sellerId = sellerId
      if (buyerId) params.buyerId = buyerId
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/invoicenote',
        params,
        paramsSerializer: p => qs.stringify(p, { indices: false })
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async searchPartyByName (_, { tradeName, projectId }) {
    try {
      const response = await axios({
        method: 'GET',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/party',
        params: { tradeName, projectId, size: 1 }
      })
      const content = response.data?.content
      return Array.isArray(content) && content.length > 0 ? content[0] : null
    } catch (error) {
      throw error.response
    }
  },

  async generateCoacsu (_, { coacsuId }) {
    try {
      const response = await axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: `/coacsu/${coacsuId}/generateCoacsu`,
        data: { coacsuLineChanged: false, newStatus: 'SENT' },
        headers: { 'Content-Type': 'application/json' }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async updateCoacsu ({ rootState }, { coacsuId, request, activeLang }) {
    try {
      const response = await axios({
        method: 'PUT',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: `/coacsu/${coacsuId}`,
        data: request,
        headers: {
          'Accept-Language': activeLang || (rootState.session?.lang) || 'es',
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async checkDuplicate ({ rootState }, { invoiceNoteIds, sellerId, buyerId, centreBuyerInvoiceeCode, activeLang }) {
    try {
      const response = await axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/coacsu/check-duplicate',
        data: { invoiceNoteIds, sellerId, buyerId, centreBuyerInvoiceeCode },
        headers: {
          'Accept-Language': activeLang || rootState.session?.lang || 'es',
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  },

  async previewCoacsuDetail ({ rootState }, { invoiceNoteIds, sellerId, buyerId, centreBuyerInvoiceeCode, activeLang }) {
    try {
      const data = { invoiceNoteIds, sellerId, buyerId }
      if (centreBuyerInvoiceeCode) data.centreBuyerInvoiceeCode = centreBuyerInvoiceeCode
      const response = await axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_NEW_SERVICE_URL,
        url: '/coacsu/preview-detail',
        data,
        headers: {
          'Accept-Language': activeLang || rootState.session?.lang || 'es',
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      throw error.response
    }
  }
}

// -----------------------------
// mutations
// -----------------------------
const mutations = {
  resetSessionCoacsu (state) {
    state.session = initSession()
    state.local = initLocal()
  },
  'COACSU_STATE' (state, payload) {
    state.session.coacsu = { ...state.session.coacsu, ...payload }
  },
  'COACSU_DETAIL_STATE' (state, payload) {
    state.session.coacsuDetail = payload
  },
  'SET_COACSU_DRAFT' (state, payload) {
    state.session.coacsuDraft = payload
  },
  'QUERY_COACSU_STATE' (state, payload) {
    state.session.queryCoacsu = payload
  },
  'LIST_SELECTED_FILTERS_COACSU_STATE' (state, payload) {
    state.session.listSelectedFiltersCoacsu = payload
  },
  // combos
  'OPTIONS_FILTER_SERVICE_COACSU' (state, payload) {
    state.session.optionsFilterServiceCoacsu = payload
  },
  'OPTIONS_FILTER_SELLER_COMPANY_COACSU' (state, payload) {
    state.session.optionsFilterSellerCompanyCoacsu = payload
  },
  'OPTIONS_FILTER_BUYER_COMPANY_COACSU' (state, payload) {
    state.session.optionsFilterBuyerCompanyCoacsu = payload
  },
  'OPTIONS_FILTER_POINT_OF_SALE_COACSU' (state, payload) {
    state.session.optionsFilterPointOfSaleCoacsu = payload
  },
  'OPTIONS_FILTER_DOCUMENT_NUMBER_COACSU' (state, payload) {
    state.session.optionsFilterDocumentNumberCoacsu = payload
  },
  'OPTIONS_FILTER_SALE_DATE_COACSU' (state, payload) {
    state.session.optionsFilterSaleDateCoacsu = payload
  },
  'OPTIONS_FILTER_ARTICLE_CODE_COACSU' (state, payload) {
    state.session.optionsFilterArticleCodeCoacsu = payload
  },

  // columnas tabla principal
  'IS_SHOW_SERVICE_COACSU' (state, payload) {
    state.local.isShowServiceCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowServiceCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_SELLER_COMPANY_COACSU' (state, payload) {
    state.local.isShowSellerCompanyCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowSellerCompanyCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_BUYER_COMPANY_COACSU' (state, payload) {
    state.local.isShowBuyerCompanyCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowBuyerCompanyCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_POINT_OF_SALE_COACSU' (state, payload) {
    state.local.isShowPointOfSaleCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowPointOfSaleCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_DOCUMENT_NUMBER_COACSU' (state, payload) {
    state.local.isShowDocumentNumberCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowDocumentNumberCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_DOCUMENT_DATE_COACSU' (state, payload) {
    state.local.isShowDocumentDateCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowDocumentDateCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_SALE_DATE_COACSU' (state, payload) {
    state.local.isShowSaleDateCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowSaleDateCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_ARTICLE_CODE_COACSU' (state, payload) {
    state.local.isShowArticleCodeCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowArticleCodeCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_QUANTITY_SOLD_COACSU' (state, payload) {
    state.local.isShowQuantitySoldCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowQuantitySoldCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_QUANTITY_RETURNED_COACSU' (state, payload) {
    state.local.isShowQuantityReturnedCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowQuantityReturnedCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_CREATION_DATE_TIME_COACSU' (state, payload) {
    state.local.isShowCreationDateTimeCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowCreationDateTimeCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  'IS_SHOW_TOTAL_AMOUNT_COACSU' (state, payload) {
    state.local.isShowTotalAmountCoacsu = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    storeuser.coacsu.isShowTotalAmountCoacsu = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },

  // -----------------------------
  // DETALLE (NUEVO)
  // -----------------------------
  SET_COACSU_DETAIL (state, payload) {
    state.session.coacsu.detail = payload
  },
  SET_SHOW_LINE_NUMBER (state, payload) {
    state.local.columnsDetailCoacsu.lineNumber = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.lineNumber = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  SET_SHOW_INVOICE_NUMBER (state, payload) {
    state.local.columnsDetailCoacsu.invoiceNumber = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.invoiceNumber = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  SET_SHOW_ISSUE_DATE (state, payload) {
    state.local.columnsDetailCoacsu.issueDate = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.issueDate = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  SET_SHOW_PAYMENT_DATE (state, payload) {
    state.local.columnsDetailCoacsu.paymentDate = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.paymentDate = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  SET_SHOW_TOTAL (state, payload) {
    state.local.columnsDetailCoacsu.total = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.total = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  },
  SET_SHOW_CURRENCY (state, payload) {
    state.local.columnsDetailCoacsu.currency = payload
    const storeuser = JSON.parse(localStorage.getItem(state.userConfigLocalStorage))
    if (!storeuser.coacsu) storeuser.coacsu = {}
    if (!storeuser.coacsu.columnsDetailCoacsu) storeuser.coacsu.columnsDetailCoacsu = {}
    storeuser.coacsu.columnsDetailCoacsu.currency = payload
    localStorage.setItem(state.userConfigLocalStorage, JSON.stringify(storeuser))
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
